import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, Bed, IndianRupee, Plus, Check, Calendar, Search } from 'lucide-react';
import { rooms as staticRooms } from '../data/mockData';
import axios from 'axios';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Rooms = () => {
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleCheckAvailability = async () => {
    if (!checkIn || !checkOut) {
      return;
    }

    setLoading(true);
    setSearched(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/rooms/pricing`, {
        params: { checkIn, checkOut }
      });
      
      const pricedRooms = response.data.map(pricedRoom => {
        const staticRoom = staticRooms.find(r => r.id === pricedRoom.id);
        return {
          ...staticRoom,
          ...pricedRoom,
          displayPrice: pricedRoom.price_per_night,
          totalPrice: pricedRoom.total_price,
          numNights: pricedRoom.num_nights,
          hasCustomPricing: pricedRoom.has_custom_pricing
        };
      });
      
      setRooms(pricedRooms);
    } catch (error) {
      console.error('Error fetching pricing:', error);
      setRooms([]);
    } finally {
      setLoading(false);
    }
  };

  const minDate = new Date().toISOString().split('T')[0];

  return (
    <div className="min-h-screen">
      <SEO 
        title="Varanasi Hotel Rooms & Pricing — Deluxe, Standard & Group Rooms"
        description="Book affordable hotel rooms in Varanasi near Kashi Vishwanath Temple. Deluxe rooms ₹1500/night, Standard ₹1200/night, Ultra-Large ₹2500/night for groups up to 10. AC, WiFi, attached bathroom included."
        keywords="varanasi hotel rooms, hotel room price varanasi, budget rooms varanasi, family rooms varanasi, group accommodation varanasi, cheap hotel near kashi vishwanath temple, varanasi hotel booking online, deluxe room varanasi, hotel room near ganga ghat, affordable hotel varanasi, couple room varanasi, dormitory varanasi, varanasi room tariff, hotel varanasi price list"
        url="https://www.vedhomestays.com/rooms"
        breadcrumbs={[
          { name: "Home", url: "https://www.vedhomestays.com/" },
          { name: "Rooms & Pricing", url: "https://www.vedhomestays.com/rooms" }
        ]}
      />
      
      {/* Hero Section */}
      <section className="relative h-80 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1561361058-c24cecae35ca"
            alt="Varanasi View"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 to-black/50"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Hotel Rooms in Varanasi</h1>
          <p className="text-xl md:text-2xl text-orange-300">Check availability & pricing near Kashi Vishwanath Temple</p>
        </div>
      </section>

      {/* Date Selection */}
      <section className="py-12 bg-gradient-to-b from-white to-orange-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-orange-100">
              <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center justify-center">
                <Calendar className="mr-3 text-orange-600" size={32} />
                Check Availability & Pricing
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Check-in Date *
                  </label>
                  <input
                    type="date"
                    value={checkIn}
                    onChange={(e) => setCheckIn(e.target.value)}
                    min={minDate}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                  <p className="text-xs text-gray-600 mt-1">Check-in: 11:00 AM</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Check-out Date *
                  </label>
                  <input
                    type="date"
                    value={checkOut}
                    onChange={(e) => setCheckOut(e.target.value)}
                    min={checkIn || minDate}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                  <p className="text-xs text-gray-600 mt-1">Check-out: 10:00 AM</p>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={handleCheckAvailability}
                    disabled={!checkIn || !checkOut || loading}
                    className="w-full px-6 py-3 bg-orange-600 text-white rounded-lg font-semibold text-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    <Search size={20} />
                    <span>{loading ? 'Searching...' : 'Check Availability'}</span>
                  </button>
                </div>
              </div>
              {checkIn && checkOut && !loading && rooms.length > 0 && (
                <div className="mt-4 text-center text-sm text-gray-700 bg-green-50 rounded-lg p-3 border border-green-200">
                  <strong className="text-green-700">✓ Showing available rooms</strong> from {new Date(checkIn).toLocaleDateString()} to {new Date(checkOut).toLocaleDateString()}
                  ({rooms[0].numNights} night{rooms[0].numNights > 1 ? 's' : ''})
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Loading State */}
      {loading && (
        <section className="py-20">
          <div className="container mx-auto px-4 text-center">
            <div className="max-w-md mx-auto">
              <div className="inline-block w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mb-6"></div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Searching for Rooms...</h3>
              <p className="text-gray-600">Finding the best available rooms for your dates</p>
            </div>
          </div>
        </section>
      )}

      {/* Rooms Grid */}
      {searched && !loading && rooms.length > 0 && (
        <section className="py-20 bg-gradient-to-b from-orange-50 to-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-800 mb-3">Available Rooms</h2>
              <p className="text-gray-600">We found {rooms.length} room{rooms.length > 1 ? 's' : ''} available for your dates</p>
            </div>
            <div className="max-w-7xl mx-auto space-y-12">
            {rooms.map((room, index) => (
              <div
                key={room.id}
                className={`bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow ${
                  index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                } flex flex-col lg:flex`}
              >
                {/* Image Gallery */}
                <div className="lg:w-1/2">
                  <div className="grid grid-cols-2 h-full">
                    {room.images.map((image, imgIndex) => (
                      <div
                        key={imgIndex}
                        className="relative h-64 lg:h-full overflow-hidden group"
                      >
                        <img
                          src={image}
                          alt={`${room.name} - ${imgIndex + 1}`}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Room Details */}
                <div className="lg:w-1/2 p-8 lg:p-10 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-3xl font-bold text-gray-800">{room.type}</h2>
                      {room.available && (
                        <span className="bg-green-100 text-green-700 px-4 py-1.5 rounded-full text-sm font-semibold">
                          Available
                        </span>
                      )}
                    </div>

                    {/* Pricing */}
                    <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-6 rounded-xl mb-6">
                      <div className="flex items-baseline mb-3">
                        <IndianRupee className="text-orange-600" size={28} />
                        <span className="text-4xl font-bold text-gray-800">
                          {Math.round(room.displayPrice || room.price)}
                        </span>
                        <span className="text-gray-600 ml-2">/night</span>
                      </div>
                      {room.totalPrice && room.numNights && (
                        <div className="mb-3 text-sm">
                          <span className="text-gray-700">
                            Total for {room.numNights} night{room.numNights > 1 ? 's' : ''}: 
                          </span>
                          <span className="font-bold text-orange-600 ml-2">₹{Math.round(room.totalPrice)}</span>
                          {room.hasCustomPricing && (
                            <span className="ml-2 text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">
                              Custom Pricing
                            </span>
                          )}
                        </div>
                      )}
                      <div className="space-y-2 text-sm text-gray-700">
                        <div className="flex items-center">
                          <Bed size={16} className="mr-2 text-orange-600" />
                          <span>{room.bedType}</span>
                        </div>
                        <div className="flex items-center">
                          <Users size={16} className="mr-2 text-orange-600" />
                          <span>Base: {room.baseOccupancy} guests | Max: {room.maxOccupancy} guests</span>
                        </div>
                        {room.extraMattressCharge > 0 && (
                          <div className="flex items-center">
                            <Plus size={16} className="mr-2 text-orange-600" />
                            <span>Extra mattress: ₹{room.extraMattressCharge}/person</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Amenities */}
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-3">Room Amenities</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {room.amenities.map((amenity, idx) => (
                          <div key={idx} className="flex items-center text-gray-700">
                            <Check size={18} className="text-green-600 mr-2 flex-shrink-0" />
                            <span className="text-sm">{amenity}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* CTA Button */}
                  <Link
                    to="/contact"
                    className="block w-full text-center px-6 py-4 bg-orange-600 text-white rounded-xl font-semibold text-lg hover:bg-orange-700 transition-colors"
                  >
                    Book This Room
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {/* Additional Info */}
          <div className="mt-16 max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">
                Important Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Check-in / Check-out</h4>
                  <p className="text-gray-600 text-sm">Check-in: 11:00 AM</p>
                  <p className="text-gray-600 text-sm">Check-out: 10:00 AM</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Cancellation Policy</h4>
                  <p className="text-gray-600 text-sm">Free cancellation up to 24 hours before check-in</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Payment</h4>
                  <p className="text-gray-600 text-sm">Cash, UPI, and online payments accepted</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Extra Services</h4>
                  <p className="text-gray-600 text-sm">Airport pickup, local guide, and tour arrangements available</p>
                </div>
              </div>
              <div className="mt-6 p-4 bg-orange-50 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>Note:</strong> Prices are configurable and may vary based on season and booking platform. 
                  Contact us for current rates and special offers.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* No Results */}
      {searched && !loading && rooms.length === 0 && (
        <section className="py-20">
          <div className="container mx-auto px-4 text-center">
            <div className="max-w-md mx-auto bg-yellow-50 border-2 border-yellow-200 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-gray-800 mb-4">No Rooms Available</h3>
              <p className="text-gray-600 mb-4">
                Sorry, we don't have any rooms available for the selected dates.
              </p>
              <p className="text-sm text-gray-600">
                Please try different dates or contact us directly at <strong>+91 9984397001</strong>
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Initial State - Before Search */}
      {!searched && (
        <section className="py-20 bg-gradient-to-b from-orange-50 to-white">
          <div className="container mx-auto px-4 text-center">
            <div className="max-w-2xl mx-auto">
              <div className="mb-8">
                <Calendar size={80} className="mx-auto text-orange-600 mb-6" />
                <h3 className="text-3xl font-bold text-gray-800 mb-4">
                  Check Hotel Room Availability in Varanasi
                </h3>
                <p className="text-lg text-gray-600">
                  Choose your check-in and check-out dates above to view available rooms with exact pricing at Ved Homestays, located near Kashi Vishwanath Temple.
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-md p-8">
                <h4 className="text-xl font-bold text-gray-800 mb-4">Room Types at Ved Homestays Varanasi</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                  <div className="p-4 bg-orange-50 rounded-lg">
                    <h5 className="font-semibold text-gray-800 mb-2">🛏️ Deluxe Rooms (2 units) — ₹1500/night</h5>
                    <p className="text-sm text-gray-600">Queen bed, AC, WiFi, attached bathroom, up to 5 guests. Extra mattress available.</p>
                  </div>
                  <div className="p-4 bg-orange-50 rounded-lg">
                    <h5 className="font-semibold text-gray-800 mb-2">💎 Standard Room (1 unit) — ₹1200/night</h5>
                    <p className="text-sm text-gray-600">Queen bed, AC, WiFi, attached bathroom. Perfect for couples visiting Varanasi.</p>
                  </div>
                  <div className="p-4 bg-orange-50 rounded-lg md:col-span-2">
                    <h5 className="font-semibold text-gray-800 mb-2">🏛️ Ultra-Large Room (1 unit) — ₹2500/night</h5>
                    <p className="text-sm text-gray-600">6 single beds, AC, WiFi, attached bathroom, up to 10 guests — perfect for families, groups & pilgrim parties visiting Kashi Vishwanath Temple.</p>
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-4">
                  All rooms include: Air Conditioning, Free WiFi, Attached Bathroom, 24/7 Hot Water, Room Service. 
                  Located 5 minutes walk from Kashi Vishwanath Temple and Dashashwamedh Ghat (Ganga Aarti).
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      {searched && !loading && rooms.length > 0 && (
        <section className="py-16 bg-gradient-to-r from-orange-600 to-amber-600 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Book Your Perfect Room?
            </h2>
            <p className="text-lg mb-6 max-w-2xl mx-auto">
              Contact us now for availability, special rates, and booking assistance
            </p>
            <Link
              to="/contact"
              className="inline-block px-10 py-4 bg-white text-orange-600 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors"
            >
              Book Now
            </Link>
          </div>
        </section>
      )}
    </div>
  );
};

export default Rooms;
